import { z } from 'zod';
import { PHONE_STRINGS } from '@/lib/constants/phone';
import { phonePayload, phoneSchema, toPhoneFormFields } from '@/lib/utils/phone.utils';
import type {
  ContactPreference,
  PartnerProfileData,
  PartnerProfilePayload,
} from '../../api/partner-profile.api';
import { PARTNER_PROFILE_STRINGS as S } from './partner-profile.strings';

export const CONTACT_PREFERENCES = [
  'email',
  'scheduling',
] as const satisfies readonly ContactPreference[];

const optionalUrl = z.union([z.string().trim().url('Enter a valid URL'), z.literal('')]).optional();

/** Any booking URL, but it must be http(s): the Backend refuses other schemes, and the Schedule button opens it (A-107). */
const optionalWebUrl = z
  .union([
    z
      .string()
      .trim()
      .url('Enter a valid URL')
      .refine((value) => /^https?:\/\//i.test(value), 'Enter a valid URL starting with http:// or https://'),
    z.literal(''),
  ])
  .optional();

/**
 * Trusted Partner profile edit-mode form (FR-28). Required/optional exactly
 * matches `docs/reference/data-fields.md`'s "Trusted Partner Page" row - the
 * Backend re-validates everything regardless (never trust the client). No
 * `twitterUrl` field: shown on the Figma edit-mode screen but absent from
 * data-fields.md, which outranks Figma.
 */
export const partnerProfileSchema = z
  .object({
    businessName: z.string().trim().min(1, 'Business Name is required'),
    contactName: z.string().trim().min(1, 'Contact Name is required'),
    contactTitle: z.string().trim().min(1, 'Contact Title is required'),
    // No `contactEmail` field here - it's locked to the caller's own account
    // email (IdentitySection renders it read-only, never `register()`ed) and
    // is never part of the submitted payload; see `partner-profile.api.ts`'s
    // doc comment on `PartnerProfileData.contactEmail`.
    inquiryEmail: z
      .string()
      .trim()
      .min(1, 'Contact Email for Inquiries is required')
      .email('Enter a valid email'),
    // Required, but a legacy free-text phone the user has not replaced counts as present
    // (`legacyPhone`, form-only, like the stored-image flags below): the empty-and-no-legacy
    // check is in the superRefine, so an untouched legacy value never blocks a save (MYN-277).
    phone: phoneSchema(false),
    legacyPhone: z.string().optional(),
    websiteUrl: z.string().trim().min(1, 'Website is required').url('Enter a valid URL'),
    schedulingUrl: optionalWebUrl,
    contactPreference: z.enum(CONTACT_PREFERENCES),
    description: z.string().trim().min(1, 'Bio / Introduction is required'),
    // Logo and detail cover are REQUIRED (FR-116, data-fields.md). The key fields are
    // write-only and empty unless a new file was picked, so "required" means "a new
    // key OR an image already stored" - the two `hasStored...` flags below carry the
    // second half. They are form-only and never sent (`toApiPayload`).
    logoFileKey: z.string().trim().optional(),
    coverPhotoFileKey: z.string().trim().optional(),
    hasStoredLogo: z.boolean(),
    hasStoredCover: z.boolean(),
    // Optional (FR-116): the directory-card cover, falling back to the detail cover.
    listingCoverFileKey: z.string().trim().optional(),
    promoVideoUrl: optionalUrl,
    categoryIds: z.array(z.string()).min(1, 'Select at least one service category'),
    tags: z.array(z.string()).optional(),
    tagline: z.string().trim().max(100, 'Tagline must be 100 characters or fewer').optional(),
    linkedinUrl: optionalUrl,
  })
  // Backstop for FR-108: the contact field blocks choosing scheduling with no link, but
  // the link can still be cleared afterwards. The Backend re-validates regardless.
  .superRefine((values, ctx) => {
    if (!values.phone?.trim() && !values.legacyPhone?.trim()) {
      ctx.addIssue({ code: 'custom', path: ['phone'], message: PHONE_STRINGS.REQUIRED });
    }
    if (values.contactPreference === 'scheduling' && !values.schedulingUrl?.trim()) {
      ctx.addIssue({ code: 'custom', path: ['contactPreference'], message: S.SCHEDULING_REQUIRED });
    }
    if (!values.logoFileKey?.trim() && !values.hasStoredLogo) {
      ctx.addIssue({ code: 'custom', path: ['logoFileKey'], message: S.LOGO_REQUIRED });
    }
    if (!values.coverPhotoFileKey?.trim() && !values.hasStoredCover) {
      ctx.addIssue({ code: 'custom', path: ['coverPhotoFileKey'], message: S.COVER_PHOTO_REQUIRED });
    }
  });

export type PartnerProfileFormValues = z.infer<typeof partnerProfileSchema>;

/** Maps the fetched profile onto the form's default values. */
export function toFormValues(data: PartnerProfileData): PartnerProfileFormValues {
  return {
    businessName: data.businessName ?? '',
    contactName: data.contactName ?? '',
    contactTitle: data.contactTitle ?? '',
    inquiryEmail: data.inquiryEmail ?? '',
    ...toPhoneFormFields(data.phone),
    websiteUrl: data.websiteUrl ?? '',
    schedulingUrl: data.schedulingUrl ?? '',
    contactPreference: data.contactPreference ?? 'email',
    description: data.description ?? '',
    // ALWAYS empty: these are WRITE fields, and empty means "leave the stored
    // image alone". `toApiPayload` omits an empty one entirely, which is what
    // the DTO's `@IsSubmitted()` expects.
    //
    // They used to fall back to `data.logoUrl`/`data.coverPhotoUrl`, which was
    // correct when those columns were handed back as the raw S3 key they
    // store. MYN-175 made the read side resolve them to PRESIGNED URLS, so the
    // fallback started seeding `https://...?X-Amz-Signature=...` into a field
    // validated against a key pattern. The result: uploading an image replaced
    // the bad value and passed, while any image left untouched kept its URL and
    // was rejected - so a save only worked if BOTH images were re-uploaded
    // every time. The fallback's original reason (the required-field check
    // blocking saves) no longer applies either, since the schema no longer
    // requires these.
    logoFileKey: '',
    coverPhotoFileKey: '',
    listingCoverFileKey: '',
    hasStoredLogo: Boolean(data.logoUrl),
    hasStoredCover: Boolean(data.coverPhotoUrl),
    promoVideoUrl: data.promoVideoUrl ?? '',
    categoryIds: data.categoryIds ?? [],
    tags: data.tags ?? [],
    tagline: data.tagline ?? '',
    linkedinUrl: data.linkedinUrl ?? '',
  };
}

/**
 * The stored-image flags and `legacyPhone` only drive validation and display (see the schema);
 * the API has no field for them and its strict pipe would reject them, so they never leave the
 * form. `phone` is handled separately (`phonePayload`).
 */
type FormOnlyKeys = 'hasStoredLogo' | 'hasStoredCover' | 'legacyPhone' | 'phone';

function withoutFormOnlyFields(values: PartnerProfileFormValues): Omit<PartnerProfileFormValues, FormOnlyKeys> {
  const copy: Partial<PartnerProfileFormValues> = { ...values };
  delete copy.hasStoredLogo;
  delete copy.hasStoredCover;
  delete copy.legacyPhone;
  delete copy.phone;
  return copy as Omit<PartnerProfileFormValues, FormOnlyKeys>;
}

/** Maps form values back onto the API payload shape for PATCH. */
export function toApiPayload(values: PartnerProfileFormValues): PartnerProfilePayload {
  const { logoFileKey, coverPhotoFileKey, listingCoverFileKey, schedulingUrl, ...rest } = withoutFormOnlyFields(values);
  return {
    ...rest,
    // Omitted while a legacy free-text phone is untouched, so it is neither rejected nor wiped.
    ...phonePayload(values.phone, values.legacyPhone),
    // `null` (not `''`) is what clears a stored link: the DTO treats an empty string as
    // "field omitted", so a cleared input would silently keep the old URL (FR-108).
    schedulingUrl: schedulingUrl?.trim() ? schedulingUrl : null,
    // OMITTED when no new file was picked, never sent as `''`.
    //
    // Both columns are NOT NULL, so `UpdatePartnerProfileDto` pairs
    // `@IsSubmitted()` with `@IsNotEmpty()`: omitting a key leaves the stored
    // one alone, while submitting an empty one is refused as an attempted
    // clear. Coercing `undefined` to `''` made every save look like a clear of
    // whichever image the member had not just re-uploaded - so updating only
    // the logo was rejected outright, and the only way to save was to re-upload
    // both images every time.
    ...(logoFileKey ? { logoFileKey } : {}),
    ...(coverPhotoFileKey ? { coverPhotoFileKey } : {}),
    // Optional: only sent when a new listing cover was picked (never `''`, which the DTO reads as a clear).
    ...(listingCoverFileKey ? { listingCoverFileKey } : {}),
  };
}
